/**
 * Dashboard Layout
 * Main layout with sidebar navigation and header
 */

import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { notificationService, authService} from '@smartgrow/shared';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Avatar,
    Menu,
    MenuItem,
    Badge,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    Work,
    Payment,
    RequestPage,
    People,
    PersonAdd,
    Engineering,
    Assessment,
    AccountCircle,
    Logout,
    AccountBalance,
    Agriculture,
    Receipt,
    Notifications as NotificationsIcon,
    Assignment,
    Inventory,
    FactCheck,
    Map as MapIcon,
} from '@mui/icons-material';

const drawerWidth = 260;

interface NavItem {
  title: string;
  path: string;
  icon: ReactNode;
  roles?: string[];
}

const navItems: NavItem[] = [
  { title: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
  { title: 'Projects', path: '/dashboard/projects', icon: <Work /> },
  { title: 'Farm Operations', path: '/dashboard/farm', icon: <Agriculture /> },
  { title: 'Operation Tasks', path: '/dashboard/farm-tasks', icon: <FactCheck /> },
  { title: 'Inventory', path: '/dashboard/inventory', icon: <Inventory /> },
  { title: 'Payments', path: '/dashboard/payments', icon: <Payment />, roles: ['ADMIN', 'OWNER'] },
  { title: 'Payment Requests', path: '/dashboard/payments/requests', icon: <RequestPage /> },
  { title: 'Fund Disbursement', path: '/dashboard/payments/disbursement', icon: <AccountBalance />, roles: ['ADMIN', 'OWNER'] },
  { title: 'Contracts', path: '/dashboard/contracts', icon: <Assignment />, roles: ['ADMIN', 'OWNER', 'SUPERVISOR'] },
  { title: 'Contractors', path: '/dashboard/contractors', icon: <PersonAdd /> },
  { title: 'Employees', path: '/dashboard/employees', icon: <Engineering />, roles: ['ADMIN', 'OWNER'] },
  { title: 'Expenses', path: '/dashboard/expenses', icon: <Receipt /> },
  { title: 'Notifications', path: '/dashboard/notifications', icon: <NotificationsIcon /> },
  { title: 'Reports', path: '/dashboard/reports', icon: <Assessment /> },
  { title: 'Map', path: '/dashboard/map', icon: <MapIcon /> },
  { title: 'System Users', path: '/dashboard/staff', icon: <People />, roles: ['ADMIN', 'OWNER'] },
];

export const DashboardLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const userData = localStorage.getItem('user_data');
  const user = userData ? JSON.parse(userData) : null;

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const userRole: string = user?.role ?? 'SUPERVISOR';
  const canAccess = (item: NavItem) => !item.roles || item.roles.includes(userRole);

  const drawer = (
    <Box>
      <Toolbar sx={{ backgroundColor: theme.palette.primary.main }}>
        <Typography
          variant="h6" noWrap component="div" color="white" fontWeight="bold"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          SmartGrow
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navItems.filter(canAccess).map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon>
                {item.path === '/dashboard/notifications' && unreadCount > 0 ? (
                  <Badge badgeContent={unreadCount} color="error">{item.icon}</Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {navItems.find((item) => item.path === location.pathname)?.title || 'SmartGrow'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton color="inherit" onClick={() => navigate('/dashboard/notifications')}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.fullName}
            </Typography>
            <IconButton onClick={handleProfileMenuOpen} color="inherit">
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                {user?.fullName?.substring(0, 1).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
          >
            <MenuItem onClick={() => navigate('/dashboard/profile')}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, overflowY: 'auto' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, overflowY: 'auto' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
